import type { HotelAvgRoomNight } from '../schemas/products';

/**
 * The disclaimer shown with every historical hotel average. Wording is fixed
 * by the ticket — nobody may read this figure as a quotable rate.
 */
export const AVG_ROOM_NIGHT_DISCLAIMER =
  'Average price from last bookings made. Real prices and availability have to be verified before itinerary is confirmed.';

/**
 * Below this many priced room groups the average is not used at all. One or
 * two stays say more about those particular trips than about the hotel.
 */
export const AVG_ROOM_NIGHT_MIN_SAMPLE_SIZE = 3;

/**
 * The per-room, per-night average (EUR) when it is fit to show or estimate
 * with: present, positive and drawn from enough bookings. Null otherwise —
 * no history reads as nothing, never as a zero.
 */
export const usableAvgRoomNight = (
  avg: HotelAvgRoomNight | null | undefined
): number | null => {
  if (!avg) return null;
  const { avgSellPriceEur, sampleSize } = avg;
  if (!Number.isFinite(avgSellPriceEur) || avgSellPriceEur <= 0) return null;
  if (sampleSize < AVG_ROOM_NIGHT_MIN_SAMPLE_SIZE) return null;
  return avgSellPriceEur;
};

/** The hover text: the disclaimer plus how many bookings it came from. */
export const avgRoomNightTooltip = (sampleSize: number): string =>
  `${AVG_ROOM_NIGHT_DISCLAIMER} Based on ${sampleSize} booking${sampleSize === 1 ? '' : 's'}.`;

/**
 * The upper bound (EUR, exclusive) of price levels 1–3 by the usable average
 * per room night; at or above the last one is level 4 (€€€€).
 */
export const HOTEL_PRICE_LEVEL_BOUNDS = [150, 300, 500] as const;

export type HotelPriceLevel = 1 | 2 | 3 | 4;

/** € to €€€€ from the usable average per room night; null with none. */
export const hotelPriceLevel = (
  avg: HotelAvgRoomNight | null | undefined
): HotelPriceLevel | null => {
  const perNight = usableAvgRoomNight(avg);
  if (perNight === null) return null;
  const below = HOTEL_PRICE_LEVEL_BOUNDS.findIndex((bound) => perNight < bound);
  return (below === -1 ? 4 : below + 1) as HotelPriceLevel;
};
